// The DIZZY DIAL (§14.3), the HUD's signature element: a 220 px semicircular gauge colored green →
// yellow → red with a wobbling needle on a spring, the number in Lilita One in the hub, stars that
// orbit at ≥ 40 (2/3/4 like the bean, §11.1), a red flush and sweat at ≥ 70, spiral eyes at 100.
// To its left the shove-cooldown pip, to its right the item bubble with charge pips. As a ghost
// the dial becomes the "Pebble ready" meter. Hits on the local bean kick the needle.
import { TUNE } from '@shared/constants';
import { ItemKind } from '@shared/ids';
import type { BeanView } from '../../game/FrameView';
import type { UiDeps } from '../deps';
import { actionCap } from '../glyphView';
import { icon, ITEM_ICONS } from '../icons';
import { ITEM_NAMES } from '../labels';
import { el, replayClass, setAttr, setHidden, setStyle, setText, svg, toggleClass } from '../ui';

const CX = 110;
const CY = 112;
const R = 88;
const NEEDLE = 74;
/** Needle spring (deg): stiff enough to track, underdamped so it wobbles like a toy gauge. */
const SPRING_K = 210;
const SPRING_D = 11;
/** Idle wobble amplitude (deg) that grows with Dizzy. */
const WOBBLE_DEG = 2.5;

/** Point on the dial arc for a 0–100 value (0 = left, 100 = right). */
function arcPoint(v: number, r: number): [number, number] {
  const a = Math.PI * (1 - v / 100);
  return [CX + Math.cos(a) * r, CY - Math.sin(a) * r];
}

function arcPath(from: number, to: number, r: number): string {
  const [x0, y0] = arcPoint(from, r);
  const [x1, y1] = arcPoint(to, r);
  return `M${x0.toFixed(2)} ${y0.toFixed(2)}A${r} ${r} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

/** The needle angle in degrees for a value: −90 (left) … +90 (right). */
export function needleAngle(v: number): number {
  return -90 + Math.max(0, Math.min(100, v)) * 1.8;
}

/** Stars orbiting the dial: 0 below 40, 2 at 40, 3 at 70, 4 at 100 (§11.1 Dizzy tiers). */
export function dizzyStars(d: number): number {
  return d >= TUNE.dizzy.spiralAt ? 4 : d >= TUNE.dizzy.flushAt ? 3 : d >= TUNE.dizzy.starsAt ? 2 : 0;
}

export function dizzyTier(d: number): 'calm' | 'warm' | 'hot' | 'max' {
  return d >= TUNE.dizzy.spiralAt ? 'max' : d >= TUNE.dizzy.flushAt ? 'hot' : d >= TUNE.dizzy.starsAt ? 'warm' : 'calm';
}

export interface DizzyDial {
  readonly el: HTMLElement;
  /** A hit on the local bean: kicks the needle (impulse in deg/s). */
  kick(strength: number): void;
  /** The local bean lunged (shove cooldown starts) / dropped a pebble. */
  shoveUsed(nowMs: number): void;
  pebbleUsed(nowMs: number): void;
  reset(): void;
  update(me: BeanView | null, nowMs: number, dt: number): void;
}

export function createDizzyDial(deps: UiDeps): DizzyDial {
  // ---- Gauge SVG -----------------------------------------------------------------------------
  const ticks = svg('g', { class: 'dd-ticks' });
  for (let v = 0; v <= 100; v += 10) {
    const [x0, y0] = arcPoint(v, R - 15);
    const [x1, y1] = arcPoint(v, R - (v % 50 === 0 ? 25 : 21));
    ticks.append(svg('line', { x1: x0.toFixed(1), y1: y0.toFixed(1), x2: x1.toFixed(1), y2: y1.toFixed(1) }));
  }
  /** The unlit rest of the track (value → 100) under a dark veil, so the lit part glows. */
  const fillArc = svg('path', { class: 'dd-fill', d: arcPath(0.01, 100, R) });
  const ghostArc = svg('path', { class: 'dd-ghost-fill', d: arcPath(0, 0.01, R) });
  const needle = svg('g', { class: 'dd-needle' },
    svg('path', { d: `M${CX - 6} ${CY} L${CX} ${CY - NEEDLE} L${CX + 6} ${CY} Z`, class: 'dd-needle-body' }),
    svg('circle', { cx: CX, cy: CY - NEEDLE + 6, r: 3.2, class: 'dd-needle-tip' }));
  const gauge = svg('svg', { viewBox: '0 0 220 132', class: 'dd-svg', 'aria-hidden': 'true' },
    // Housing: a chunky half-disc sticker with an ink rim and a hard drop shadow.
    svg('path', { class: 'dd-shadow', d: `M${CX - R - 18} ${CY + 6}A${R + 18} ${R + 18} 0 0 1 ${CX + R + 18} ${CY + 6}Z` }),
    svg('path', { class: 'dd-housing', d: `M${CX - R - 18} ${CY}A${R + 18} ${R + 18} 0 0 1 ${CX + R + 18} ${CY}Z` }),
    svg('path', { class: 'dd-face', d: `M${CX - R + 10} ${CY}A${R - 10} ${R - 10} 0 0 1 ${CX + R - 10} ${CY}Z` }),
    // Track: ink under-stroke, then the three colored bands.
    svg('path', { class: 'dd-track-ink', d: arcPath(0, 100, R) }),
    svg('path', { class: 'dd-band green', d: arcPath(0, 40, R) }),
    svg('path', { class: 'dd-band yellow', d: arcPath(40, 70, R) }),
    svg('path', { class: 'dd-band red', d: arcPath(70, 100, R) }),
    ghostArc, fillArc, ticks, needle,
    svg('circle', { class: 'dd-hub-ring', cx: CX, cy: CY, r: 29 }));

  const num = el('span', { class: 'dd-num' });
  const unit = el('span', { class: 'dd-unit', text: '%' });
  const spiral = el('span', { class: 'dd-spiral' }, icon('spiral'));
  const hubIcon = el('span', { class: 'dd-hub-icon' }, icon('pebble'));
  const hub = el('div', { class: 'dd-hub' }, num, unit, spiral, hubIcon);
  const label = el('div', { class: 'dd-label', text: 'DIZZY' });
  const stars = el('div', { class: 'dd-stars' }, ...Array.from({ length: 4 }, (_, i) => el('i', { class: `dd-star s${i}` }, icon('star'))));
  const sweat = el('div', { class: 'dd-sweat' }, icon('sweat'), icon('sweat'));
  const dial = el('div', { class: 'dd-dial', attrs: { role: 'meter', 'aria-label': 'Dizzy', 'aria-valuemin': 0, 'aria-valuemax': 100 } },
    gauge, hub, label, stars, sweat);

  // ---- Shove-cooldown pip ------------------------------------------------------------------
  const pipFill = el('i', { class: 'pip-fill' });
  const pipCap = el('span', { class: 'pip-cap' });
  const pip = el('div', { class: 'dd-pip' }, el('div', { class: 'pip-disc' }, pipFill, icon('fist')), pipCap);

  // ---- Item bubble -------------------------------------------------------------------------
  const itemIcon = el('span', { class: 'ib-icon' });
  const itemPips = el('span', { class: 'ib-pips' });
  const itemName = el('span', { class: 'ib-name' });
  const itemCap = el('span', { class: 'ib-cap' });
  const item = el('div', { class: 'dd-item empty' }, el('div', { class: 'ib-disc' }, itemIcon), itemPips, itemName, itemCap);

  const root = el('div', { class: 'dizzy-dial' }, pip, dial, item);

  let angle = needleAngle(0);
  let vel = 0;
  let shownNum = -1;
  let tier = '';
  let starCount = -1;
  let itemKey = '';
  let lastShove = -Infinity;
  let lastPebble = -Infinity;
  let pipReady = true;
  let ghost = false;
  let capDevice = '';

  function renderCaps(): void {
    pipCap.replaceChildren(actionCap(deps, 'shove'));
    itemCap.replaceChildren(actionCap(deps, 'item'));
  }

  function setFill(path: SVGPathElement, v: number): void {
    const c = Math.max(0.01, Math.min(100, v));
    setAttr(path, 'd', arcPath(0, c, R));
  }

  /** Veils the track from `v` to 100 (the unlit part). */
  function setVeil(v: number): void {
    const c = Math.max(0, Math.min(99.9, v));
    setAttr(fillArc, 'd', arcPath(c, 100, R));
  }

  return {
    el: root,
    kick(strength) {
      vel += strength;
      replayClass(dial, 'dd-hit');
    },
    shoveUsed(now) {
      lastShove = now;
    },
    pebbleUsed(now) {
      lastPebble = now;
    },
    reset() {
      angle = needleAngle(0);
      vel = 0;
      lastShove = -Infinity;
      lastPebble = -Infinity;
    },
    update(me, now, dt) {
      const dev = deps.services.input.device;
      if (dev !== capDevice) {
        capDevice = dev;
        renderCaps();
      }
      const active = me !== null && me.active && !me.out;
      const isGhost = me !== null && me.active && me.ghost;
      setHidden(root, !active && !isGhost);
      if (!active && !isGhost) return;
      if (isGhost !== ghost) {
        ghost = isGhost;
        toggleClass(root, 'ghost', ghost);
        setText(label, ghost ? 'PEBBLE' : 'DIZZY');
      }
      const step = Math.min(0.05, Math.max(0, dt));
      if (ghost) {
        // Pebble meter: fills over the 6 s cooldown, "READY!" when full.
        const cd = TUNE.ghost.pebbleCooldownTicks / 60;
        const k = Math.min(1, (now - lastPebble) / 1000 / cd);
        setFill(ghostArc, k * 100);
        const ready = k >= 1;
        toggleClass(root, 'pebble-ready', ready);
        setText(num, ready ? 'GO' : String(Math.ceil(cd * (1 - k))));
        setHidden(unit, true);
        const target = needleAngle(k * 100);
        vel += (SPRING_K * (target - angle) - SPRING_D * vel) * step;
        angle += vel * step;
        setStyle(needle as unknown as HTMLElement, 'transform', `rotate(${angle.toFixed(2)}deg)`);
        setHidden(item, true);
        setHidden(pip, true);
        return;
      }
      setHidden(unit, false);
      const d = me!.dizzy;
      const t = dizzyTier(d);
      if (t !== tier) {
        tier = t;
        dial.dataset.tier = t;
        if (t === 'max') replayClass(dial, 'dd-max-pop');
      }
      const n = Math.round(d);
      if (n !== shownNum) {
        if (n > shownNum && shownNum >= 0) replayClass(hub, 'bump');
        shownNum = n;
        setText(num, String(n));
        setAttr(dial, 'aria-valuenow', String(n));
      }
      const sc = dizzyStars(d);
      if (sc !== starCount) {
        starCount = sc;
        stars.dataset.n = String(sc);
      }
      setVeil(d);
      // Needle: spring toward the value plus a wobble that grows with Dizzy.
      const wobble = Math.sin(now / 1000 * (5 + d * 0.06)) * WOBBLE_DEG * (0.4 + d / 60);
      const target = needleAngle(d) + wobble;
      vel += (SPRING_K * (target - angle) - SPRING_D * vel) * step;
      angle += vel * step;
      angle = Math.max(-104, Math.min(104, angle));
      setStyle(needle as unknown as HTMLElement, 'transform', `rotate(${angle.toFixed(2)}deg)`);

      // Shove cooldown (§2.5: 48 ticks).
      const cdMs = (TUNE.shove.cooldownTicks / 60) * 1000;
      const k = Math.min(1, (now - lastShove) / cdMs);
      setStyle(pipFill, '--k', k.toFixed(3));
      const ready = k >= 1;
      if (ready && !pipReady) replayClass(pip, 'ready-pop');
      pipReady = ready;
      toggleClass(pip, 'cooling', !ready);
      const touch = dev === 'touch';
      setHidden(pip, touch);

      // Item bubble with charges (§14.3).
      const kind = me!.item;
      const key = `${kind}:${me!.charges}`;
      if (key !== itemKey) {
        const had = itemKey !== '' && !itemKey.startsWith(`${ItemKind.NONE}:`);
        itemKey = key;
        const empty = kind === ItemKind.NONE || me!.charges <= 0;
        toggleClass(item, 'empty', empty);
        itemIcon.replaceChildren(empty ? icon('question') : icon(ITEM_ICONS[kind]));
        setText(itemName, empty ? '' : ITEM_NAMES[kind]);
        itemPips.replaceChildren(...(empty ? [] : Array.from({ length: Math.max(1, me!.charges) }, () => el('i'))));
        if (!empty && !had) replayClass(item, 'got');
      }
      setHidden(item, touch);
    },
  };
}
